import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Buttons, FlexContainer, Text, Spinner } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { userStageSelector } from 'Store/modules/validations/selectors';
import useTranslation from 'Hooks/useTranslation';
import { useFetchPreviousYear, useFetchPreviousYearIsRequest } from 'Containers/Dashboard/hooks';
import { PreviousYearStatus } from 'Containers/PreviousYears/types';
import { redirectToAppropriateRoute } from 'Containers/PreviousYears/utils';
import { routes } from 'Constants/routes';
import { stages } from 'Constants/stages';
import { PREVIOUS_YEARS_HELP_URL } from 'Constants/helpsUrl';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import * as Styled from './styles';
import { useFetchUpdatePreviousYearsRequest } from './hooks';

const PreviousYearsInfo = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.PY);
  const userStage = useSelector(userStageSelector);
  const { data, isLoading: isLoadingFetch } = useFetchPreviousYear();
  const { data: isRequest, isLoading: loading } = useFetchPreviousYearIsRequest();
  const { mutate, isLoading: isLoadingUpdate } = useFetchUpdatePreviousYearsRequest(data);
  const isLoading = isLoadingUpdate || isLoadingFetch || loading;

  useEffect(() => {
    redirectToAppropriateRoute(isRequest, data);
  }, [data, isRequest]);

  const handleClick = (): void => {
    if (data && data?.stage === PreviousYearStatus.SUBMITTED && isRequest?.isRequested) {
      history.push(routes.PREVIOUS_YEARS_WORKING);
    } else if (data?.stage === PreviousYearStatus.READY) {
      mutate({});
    } else if (userStage === stages.DATA_RETRIEVAL || !userStage) {
      history.push(routes.DATAGATHERING_FIRST_STEP, {
        products: {
          isPreviousYear: true,
        },
      });
    } else {
      mutate({});
    }
  };

  return (
    <Styled.ThreeColLayout>
      <Styled.TitleContainer type="headline" size="S_L_S" color="dark">
        {t('PreviousYears.title')}
      </Styled.TitleContainer>
      <FlexContainer direction="column" gap="dimension_24">
        <Styled.Title type="headline" size="S_L_S" color="dark">
          {t('PreviousYears.title')}
        </Styled.Title>
        <Styled.ContainerInProgress>
          {isLoading ? (
            <Styled.SpinnerContainer>
              <Spinner size="L" justify="center" align="center" />
            </Styled.SpinnerContainer>
          ) : (
            <FlexContainer direction="column" align="center" justify="center">
              <img
                src={`${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/search_s.png`}
                alt="continueAnalytics"
              />
              <Styled.DescriptionContainer direction="column" gap="dimension_16">
                <Text align="center" type="paragraphs" size="L_XL_S" color="dark">
                  {t('PreviousYears.info.title')}
                </Text>
                <Text align="center" type="paragraphs" size="S_M_M" color="dark">
                  {t('PreviousYears.info.description')}
                </Text>
                <Buttons.Classic
                  mode="primary"
                  label={t('PreviousYears.continue.analitycs')}
                  size="m"
                  onClick={handleClick}
                />
              </Styled.DescriptionContainer>
              <Styled.Informative
                description={t('PreviousYears.Informative.description')}
                helpLink={{
                  title: t('PreviousYears.Informative.helpButton'),
                  url: PREVIOUS_YEARS_HELP_URL,
                }}
                mode="light"
                noClose
                title={t('PreviousYears.Informative.title')}
              />
            </FlexContainer>
          )}
        </Styled.ContainerInProgress>
      </FlexContainer>
    </Styled.ThreeColLayout>
  );
};

export default PreviousYearsInfo;
